/**
 * Pure progress math for the live build experience (docs/live-build.md §2).
 * No imports beyond types — this module must stay trivially unit-testable
 * and safe to import from both the server and the web client.
 */
import type { BriefTask, ProposalBuildStage, ProposalBuildStageState } from './api.js';

export const STAGE_WEIGHTS: Record<ProposalBuildStage, number> = {
  strategy: 0.42,
  assembly: 0.36,
  imagery: 0.1,
  quality: 0.12,
};

export const STAGE_MILESTONES = {
  strategy: { brief_loaded: 0.1, strategy_authored: 0.55, composition_ready: 1.0 },
  assembly: {
    build_started: 0.1,
    page_plan: 0.25,
    pages_assembled: 0.55,
    cleanup: 0.7,
    copy_written: 0.95,
    settled: 1.0,
  },
  imagery: { search_started: 0.2, assets_selected: 0.55, images_applied: 1.0 },
  quality: { document_audit: 0.3, composition_audit: 0.6, proposal_generated: 0.85, run_completed: 1.0 },
} as const satisfies Record<ProposalBuildStage, Record<string, number>>;

/**
 * Overall 0..100 integer progress, weighted by STAGE_WEIGHTS. Never derived
 * from turn count, tool count, elapsed time or tokens — only from each
 * stage's own 0..1 milestone progress.
 */
export function overallProgress(stages: ProposalBuildStageState[]): number {
  const total = stages.reduce((sum, stage) => {
    const weight = STAGE_WEIGHTS[stage.id] ?? 0;
    const progress = Math.max(0, Math.min(1, stage.progress));
    return sum + weight * progress;
  }, 0);
  return Math.round(Math.max(0, Math.min(1, total)) * 100);
}

/** Never rewind — a snapshot's progress can only hold or advance. */
export function monotonic(previous: number, next: number): number {
  return Math.max(previous, next);
}

/** Resolved brief rows / all brief rows, 0..100 integer. */
export function briefProgress(tasks: BriefTask[]): number {
  const rows = tasks.flatMap((task) => task.rows);
  if (rows.length === 0) return 0;
  const resolved = rows.filter((row) => row.value !== null).length;
  return Math.round((resolved / rows.length) * 100);
}
